import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons/faTriangleExclamation";
import { Dialog, DialogActions, DialogContent, Grid, Tab } from "@mui/material";
import { MprcBarTabs, MprcButton, MprcContainer, MprcDialogTitleClose, MprcIconFa, MprcRequestErrorDialog, MprcRequestIndicator } from "components";
import { ButtonCancel, ButtonSave } from "components/buttons";
import { MprcResolveRequestErrors } from "components/MprcRequestErrorReport";
import { MprcSwitch } from "components/MprcSwitch";
import { ManagedQuestionAlternativeDto, ManagedQuestionEvaluationDto, ManagedQuestionResponse, ManagedQuestionSurveyDto, QuestionClass, QuestionType, SaveQuestionCommand } from "generated/CoreApi_v1";
import LibUtils from "lib/LibUtils";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from 'uuid';
import { ALTERNATIVE_FALSE, ALTERNATIVE_TRUE, AddQuestionOptions } from "views/assessment/common/assessment";
import { IPostValidationAvailableQuestion, IValidationFieldsAvailableQuestion, validadeAvailableQuestion } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionAvailableQuestion";
import { EditQuestionEssay, IPostValidationEssay, IValidationFieldsEssay, validateEssay } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionEssay";
import { EditQuestionFillIn, IPostValidationFillIn, IValidationFieldsFillIn, validateFillIn } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionFillIn";
import { EditQuestionMainbox, IPostValidationMainBox, IValidationFieldsMainBox, validadeMainBox } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionMainbox";
import { EditQuestionStar, IPostValidationStar, IValidationFieldsStar, validateStar } from "views/assessment/questionBank/Home/EditQuestion/EditQuestionStar";
import { EditQuestionPointOthers, IPostValidationPointOther, IValidationFieldsPointOthers, validatePointOther } from "views/assessment/questionBank/Home/EditQuestion/Evaluation/EditQuestionPointOthers";
import { EditQuestionClassification } from "views/assessment/questionBank/Home/EditQuestion/Survey/EditQuestionClassification";
import { EditQuestionAlternative, IPostValidationAlternatives, IValidationFieldsAlternative, validateAlternatives } from "./EditQuestionAlternative";
import { EditQuestionComplement } from "./EditQuestionComplement";
import { EditQuestionFeedback, IPostValidationFeedback } from "./EditQuestionFeedback";

////////////////////////////////////////////////////////////////////////////////

type QuestionTabOptions = "complement" | "alternative" | "essay" | "fillIn" | "feedback" | "pointOthers" | "classification";

type SavingOptions = "save" | "draft";

export type QuestionEditMode = "default" | "challenge";

interface IBoxValidations {
    mainBox: boolean;
    alternativeBox: boolean;
    starBox: boolean;
    essayBox: boolean;
    fillInBox: boolean;
    feedbackBox: boolean;
    pointBox: boolean;
    available: boolean;
}

export interface IValidationFields {
    mainBox: IValidationFieldsMainBox;
    alternativeBox: IValidationFieldsAlternative;
    starBox: IValidationFieldsStar;
    essayBox: IValidationFieldsEssay;
    fillInBox: IValidationFieldsFillIn;
    pointOtherBox: IValidationFieldsPointOthers;
    available: IValidationFieldsAvailableQuestion;
}

export interface IPostValidation {
    mainBox: IPostValidationMainBox;
    alternativeBox: IPostValidationAlternatives;
    starBox: IPostValidationStar;
    essayBox: IPostValidationEssay;
    fillInBox: IPostValidationFillIn;
    feedbackBox: IPostValidationFeedback;
    pointOtherBox: IPostValidationPointOther;
    available: IPostValidationAvailableQuestion;
}

interface IQuestionTab {
    value: QuestionTabOptions;
    name: string;
    hasError: boolean;
}

export type QuestionContextType = {
    viewData: ManagedQuestionResponse;
    postValidation: IPostValidation;
    validationFields: IValidationFields;
    forSurvey: boolean;
    readonly?: boolean;
    editMode: QuestionEditMode;
    handleUpdateRecord: (name: keyof ManagedQuestionResponse, v: any) => void;
    handleUpdateEvaluation: (name: keyof ManagedQuestionEvaluationDto, v: any) => void;
    handleUpdateSurvey: (name: keyof ManagedQuestionSurveyDto, v: any) => void;
};

export const QuestionContext = React.createContext<QuestionContextType>(undefined!);

////////////////////////////////////////////////////////////////////////////////

export const useQuestionContext = () => React.useContext(QuestionContext);

////////////////////////////////////////////////////////////////////////////
/// FUNCTIONS
////////////////////////////////////////////////////////////////////////////

///
export function defaultQuestionTypeByOption(addQuestionOption: AddQuestionOptions, forSurvey: boolean): QuestionType {
    return addQuestionOption == AddQuestionOptions.MultipleChoice
        ? QuestionType.SimpleChoice
        : addQuestionOption == AddQuestionOptions.Star
            ? QuestionType.Star
            : addQuestionOption == AddQuestionOptions.FillIn && forSurvey
                ? QuestionType.FillInNumber
                : addQuestionOption == AddQuestionOptions.FillIn
                    ? QuestionType.Dropdown
                    : QuestionType.Essay;
}

function getEmptyAlternatives(questionType: QuestionType): ManagedQuestionAlternativeDto[] {
    if (questionType == QuestionType.Star) {
        return [
            { alternativeId: uuidv4(), text: "5", displayOrder: 0, evaluation: null, survey: { points: 5 }, publicationRecommended: [], trainingRecommended: [] },
            { alternativeId: uuidv4(), text: "4", displayOrder: 1, evaluation: null, survey: { points: 4 }, publicationRecommended: [], trainingRecommended: [] },
            { alternativeId: uuidv4(), text: "3", displayOrder: 2, evaluation: null, survey: { points: 3 }, publicationRecommended: [], trainingRecommended: [] },
            { alternativeId: uuidv4(), text: "2", displayOrder: 3, evaluation: null, survey: { points: 2 }, publicationRecommended: [], trainingRecommended: [] },
            { alternativeId: uuidv4(), text: "1", displayOrder: 4, evaluation: null, survey: { points: 1 }, publicationRecommended: [], trainingRecommended: [] },
        ];
    } else if (questionType == QuestionType.FillInText || questionType == QuestionType.FillInNumber || questionType == QuestionType.Dropdown) {
        return [
            { alternativeId: uuidv4(), text: "", displayOrder: 0, evaluation: { feedback: null, correctAnswer: "", answerIsNumber: false }, survey: { points: null }, publicationRecommended: [], trainingRecommended: [] },
        ];
    } else if (questionType == QuestionType.Essay) {
        return [];
    } else {
        return [
            { alternativeId: uuidv4(), text: "", displayOrder: 0, evaluation: { feedback: null, correctAnswer: ALTERNATIVE_FALSE, answerIsNumber: false }, survey: { points: null }, publicationRecommended: [], trainingRecommended: [] },
            { alternativeId: uuidv4(), text: "", displayOrder: 1, evaluation: { feedback: null, correctAnswer: ALTERNATIVE_FALSE, answerIsNumber: false }, survey: { points: null }, publicationRecommended: [], trainingRecommended: [] },
        ];
    }
}

///
export function getEmptyQuestionDto(questionType: QuestionType, forSurvey: boolean, questionGroupId?: string | null, exclusiveAssessmentId?: string | null): ManagedQuestionResponse {
    return {
        questionId: null,
        questionGroupId: questionGroupId ?? null,
        exclusiveAssessmentId: exclusiveAssessmentId ?? null,
        questionType: questionType,
        questionClass: forSurvey ? QuestionClass.Survey : QuestionClass.Evaluation,
        applyCount: 0,
        questionText: null,
        remarks: "",
        attach: null,
        thumbnailUrl: null,
        alternatives: getEmptyAlternatives(questionType),
        differentFeedback: false,
        publicationRecommended: [],
        trainingRecommended: [],
        tags: [],
        publishedOn: null,
        maxLen: 1000,
        placeholderText: null,
        availableQuestioBank: false,
        isBlocked: false,
        changeVersion: true,
        evaluation: {
            points: 1,
            randomized: false,
            allowInKnowledgeTests: questionType == QuestionType.SimpleChoice || questionType == QuestionType.MultipleChoice,
            allowPartialScore: false,
            feedback: null,
            otherIncorrectAnswers: []
        },
        survey: {
            pointTyped: false,
            showSideBySide: false,
            fillInMinValue: 0,
            fillInMaxValue: 5,
            essayAnswerRequired: false
        }
    };
}

///
function buildPostDto(questionId: string | null, dto: ManagedQuestionResponse, isDraft: boolean): SaveQuestionCommand {
    return {
        questionId,
        questionGroupId: dto.questionGroupId,
        exclusiveAssessmentId: dto.exclusiveAssessmentId,
        questionClass: dto.questionClass,
        questionText: dto.questionText ?? "",
        remarks: dto.remarks,
        questionType: dto.questionType,
        alternatives: dto.questionType == QuestionType.Essay
            ? []
            : dto.alternatives.map((item, index) => {
                return {
                    ...item,
                    displayOrder: index,
                    evaluation: item.evaluation == null ? null : { ...item.evaluation, feedback: dto.differentFeedback ? item.evaluation.feedback : null },
                    publicationRecommended: dto.differentFeedback ? item.publicationRecommended?.map(x => x.key) : [],
                    trainingRecommended: dto.differentFeedback ? item.trainingRecommended?.map(x => x.key) : []
                };
            }),
        attachId: dto.attach?.fileId ?? null,
        knowledgePublicationRecommended: dto.differentFeedback ? [] : dto.publicationRecommended?.map(x => x.key),
        trainingRecommended: dto.differentFeedback ? [] : dto.trainingRecommended?.map(x => x.key),
        tags: dto.tags.map(x => x.label),
        maxLen: dto.maxLen,
        placeholderText: dto.placeholderText,
        isBlocked: dto.isBlocked,
        availableQuestioBank: dto.availableQuestioBank,
        isDraft,
        evaluation: dto.evaluation == null || dto.questionClass != QuestionClass.Evaluation
            ? null
            : {
                points: dto.evaluation.points,
                randomized: dto.evaluation.randomized,
                allowInKnowledgeTests: dto.evaluation.allowInKnowledgeTests,
                allowPartialScore: dto.evaluation.allowPartialScore,
                feedback: dto.differentFeedback ? null : dto.evaluation.feedback,
                otherIncorrectAnswers: dto.evaluation.otherIncorrectAnswers
            },
        survey: dto.survey == null || dto.questionClass != QuestionClass.Survey
            ? null
            : {
                pointTyped: dto.survey.pointTyped,
                showSideBySide: dto.survey.showSideBySide,
                fillInMinValue: dto.survey.fillInMinValue,
                fillInMaxValue: dto.survey.fillInMaxValue,
                essayAnswerRequired: dto.survey.essayAnswerRequired,
                changeVersion: dto.changeVersion
            },
    };
}

///
export function versionHasApplyed(dto: ManagedQuestionResponse) {
    return dto.applyCount > 0;
}

///
function getDefaultTab(questionType: QuestionType): QuestionTabOptions {
    return questionType == QuestionType.SimpleChoice || questionType == QuestionType.MultipleChoice || questionType == QuestionType.Star
        ? "alternative"
        : questionType == QuestionType.Essay
            ? "essay"
            : questionType == QuestionType.Dropdown || questionType == QuestionType.FillInText || questionType == QuestionType.FillInNumber
                ? "fillIn"
                : "complement";
}

/////////////////////////////////////////////////////////////////////////////////

interface IPros {
    questionId: string | null;
    version?: number;
    questionGroupId?: string;
    exclusiveAssessmentId?: string;
    questionType: QuestionType;
    forSurvey: boolean;
    readonly?: boolean;
    editMode?: QuestionEditMode;
    onClose: (success: boolean) => void;
}

/////////////////////////////////////////////////////////////////////////////////

export const EditQuestion: React.FC<IPros> = (props) => {
    const { questionId, version, questionType, forSurvey, readonly = false, editMode = "default", onClose } = props;
    const isNewQuestion = questionId == null;

    const api = useCoreApi();
    const { t } = useTranslation("assessment");
    const { enqueueSnackbar } = useSnackbar();

    const [viewData, setViewData] = useState<ManagedQuestionResponse>(() => getEmptyQuestionDto(questionType, props.forSurvey, props.questionGroupId, props.exclusiveAssessmentId));
    const [loadDto, setLoadDto] = useState<ManagedQuestionResponse>();
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<SavingOptions | null>(null);
    const [viewFieldsErrors, setViewFieldsErrors] = useState<ApiProblemErrors>();
    const [viewError, setViewError] = useState<any>();
    const [currentTab, setCurrentTab] = useState<QuestionTabOptions>(getDefaultTab(viewData.questionType));
    const [continueCreate, setContinueCreate] = useState<boolean>(false);
    const questionTextRef = useRef<HTMLInputElement>(undefined);
    const reloadOnClose = useRef<boolean>(false);

    ///
    const handleSelectTab = (event: React.SyntheticEvent, newValue: QuestionTabOptions) => {
        setCurrentTab(newValue);
    };

    ///
    function handleUpdateRecord(name: keyof ManagedQuestionResponse, v: any) {
        setViewFieldsErrors(LibUtils.deleteObjectProp(viewFieldsErrors, name));

        if (name == "attach") {
            setViewData({ ...viewData, attach: v, thumbnailUrl: v?.url ?? null });
        }
        else if (name == "questionType" &&
            v == QuestionType.SimpleChoice &&
            viewData.alternatives.filter(x => x.evaluation?.correctAnswer == ALTERNATIVE_TRUE).length > 1) {
            var newAlternatives = viewData.alternatives.map((item) => {
                if (item.evaluation?.correctAnswer == ALTERNATIVE_TRUE)
                    item.evaluation.correctAnswer = ALTERNATIVE_FALSE;
                return item;
            });
            setViewData({ ...viewData, [name]: v, alternatives: newAlternatives });
        }
        else {
            setViewData({ ...viewData, [name]: v });
        }
    }

    ///
    function handleUpdateEvaluation(name: keyof ManagedQuestionEvaluationDto, v: any) {
        setViewFieldsErrors(LibUtils.deleteObjectProp(viewFieldsErrors, name));
        setViewData({ ...viewData, evaluation: { ...viewData.evaluation!, [name]: v } });
    }

    ///
    function handleUpdateSurvey(name: keyof ManagedQuestionSurveyDto, v: any) {
        setViewFieldsErrors(LibUtils.deleteObjectProp(viewFieldsErrors, name));
        setViewData({ ...viewData, survey: { ...viewData.survey!, [name]: v } });
    }

    ///
    const resolveErrors = (e: any | unknown) => {
        var r = MprcResolveRequestErrors(e);
        setViewFieldsErrors(r.fieldsWithError);
        setViewError(r.otherErrors);
        if (r.fieldsWithError != null) enqueueSnackbar(t("Record.SaveFailedWithFieldErrorsMessage", { ns: "shared" }), { variant: "error" });
    };

    // Validação de erros ao executar post.
    const vErrors = viewFieldsErrors ?? {};
    const postValidation: IPostValidation = {
        mainBox: {
            questionText: vErrors["questionText"],
        },
        alternativeBox: {
            alternatives: vErrors["alternatives"],
        },
        starBox: {
            alternatives: vErrors["alternatives"],
        },
        essayBox: {
            maxLen: vErrors["maxLen"],
            alternatives: vErrors["alternatives"],
        },
        fillInBox: {
            alternatives: vErrors["alternatives"],
            fillInMinValue: vErrors["fillInMinValue"],
            fillInMaxValue: vErrors["fillInMaxValue"],
        },
        feedbackBox: {
            trainingRecommended: vErrors["trainingRecommended"],
            knowledgePublicationRecommended: vErrors["knowledgePublicationRecommended"],
        },
        pointOtherBox: {
            points: vErrors["points"],
            isBlocked: vErrors["isBlocked"],
            allowInKnowledgeTests: vErrors["allowInKnowledgeTests"],
        },
        available: {
            questionGroupId: vErrors["questionGroupId"],
        }
    };

    // Define quais tabs serão apresentadas.
    const hasAlternative = questionType == QuestionType.SimpleChoice || questionType == QuestionType.MultipleChoice;
    const hasStar = questionType == QuestionType.Star;
    const hasEssay = questionType == QuestionType.Essay;
    const hasFillIn = questionType == QuestionType.FillInText || questionType == QuestionType.FillInNumber || questionType == QuestionType.Dropdown;
    const hasClassification = forSurvey;
    const hasOtherPoints = !forSurvey;
    const hasFeedback = !forSurvey && (
        questionType == QuestionType.SimpleChoice
        || questionType == QuestionType.MultipleChoice
        || questionType == QuestionType.Dropdown
        || questionType == QuestionType.FillInText
        || questionType == QuestionType.FillInNumber);

    // Validação dos campos de cada aba.
    const validationFields: IValidationFields = {
        mainBox: validadeMainBox(viewData),
        alternativeBox: validateAlternatives(viewData, forSurvey),
        starBox: validateStar(viewData),
        essayBox: validateEssay(viewData),
        fillInBox: validateFillIn(viewData, forSurvey),
        pointOtherBox: validatePointOther(viewData),
        available: validadeAvailableQuestion(viewData),
    };

    const isRecordChanged = !LibUtils.deepCompare(viewData, loadDto);

    // Verificação se existe erros nas abas.
    const boxValidation: IBoxValidations = {
        mainBox: Object.values(validationFields.mainBox).filter((x) => x === false).length == 0
            && Object.values(postValidation.mainBox).filter((x) => x !== undefined).length == 0,
        alternativeBox: !hasAlternative ? true :
            Object.values(validationFields.alternativeBox).filter((x) => x === false).length == 0
            && Object.values(postValidation.alternativeBox).filter((x) => x !== undefined).length == 0,
        starBox: !hasStar ? true :
            Object.values(validationFields.starBox).filter((x) => x === false).length == 0
            && Object.values(postValidation.starBox).filter((x) => x !== undefined).length == 0,
        essayBox: !hasEssay ? true :
            Object.values(validationFields.essayBox).filter((x) => x === false).length == 0
            && Object.values(postValidation.essayBox).filter((x) => x !== undefined).length == 0,
        fillInBox: !hasFillIn ? true :
            Object.values(validationFields.fillInBox).filter((x) => x === false).length == 0
            && Object.values(postValidation.fillInBox).filter((x) => x !== undefined).length == 0,
        feedbackBox: !hasFeedback ? true :
            Object.values(postValidation.feedbackBox).filter((x) => x !== undefined).length == 0,
        pointBox: !hasOtherPoints ? true :
            Object.values(validationFields.pointOtherBox).filter((x) => x === false).length == 0
            && Object.values(postValidation.pointOtherBox).filter((x) => x !== undefined).length == 0,
        available: Object.values(validationFields.available).filter((x) => x === false).length == 0
            && Object.values(postValidation.available).filter((x) => x !== undefined).length == 0,
    };

    const canSave = (isRecordChanged || (!isNewQuestion && viewData.publishedOn == null))
        && Object.values(boxValidation).filter((x) => x === false).length == 0;
    const canSaveDraft = isRecordChanged
        && Object.values(boxValidation).filter((x) => x === false).length == 0;
    const showDraft = viewData.exclusiveAssessmentId == null && readonly != true && (isNewQuestion || viewData.publishedOn == null)
        && version == undefined
        && editMode != "challenge";

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT's
    ////////////////////////////////////////////////////////////////////////////

    // Ao iniciar pela primeira vez...
    useEffect(() => {
        async function getData() {
            if (!isNewQuestion) {
                try {
                    const result = await api.v1.questionBank.managedGetQuestion({ questionId: questionId!, version: version ?? null });
                    setLoadDto(LibUtils.deepCopy(result.data));
                    setViewData(result.data);
                }
                catch (e) {
                    setViewError(e);
                }
                finally {
                    setLoading(false);
                }
            }
            else if (!forSurvey && viewData.evaluation != null) {
                // Definir valores default
                try {
                    const result = await api.v1.questionBank.managedQuestionDefaultValues({ questionGroupId: props.questionGroupId ?? null });

                    if (result.data) {
                        setViewData({
                            ...viewData,
                            evaluation: {
                                ...viewData.evaluation,
                                allowInKnowledgeTests: questionType == QuestionType.SimpleChoice || questionType == QuestionType.MultipleChoice
                                    ? result.data.allowInKnowledgeTests
                                    : false,
                                points: result.data.points
                            }
                        });
                    }
                }
                catch (e) {
                    setViewError(e);
                }
                finally {
                    setLoading(false);
                }
            }
            else {
                setLoading(false);
            }
        }

        if (loading) getData();
    }, [loading]);

    // Ao salvar
    useEffect(() => {
        async function postData() {
            try {
                await api.v1.questionBank.saveQuestion(buildPostDto(viewData.questionId, viewData, saving == "draft"));
                if (saving == "draft")
                    enqueueSnackbar(t("Question.Edit.DraftMessage"), { variant: "warning" });
                else {
                    enqueueSnackbar(t("Record.SaveSuccessMessage", { ns: "shared" }), { variant: "success" });
                }

                if (continueCreate) {
                    reloadOnClose.current = true;
                    setLoadDto(getEmptyQuestionDto(viewData.questionType, forSurvey));
                    var emptyDto = getEmptyQuestionDto(viewData.questionType, forSurvey, props.questionGroupId, props.exclusiveAssessmentId);
                    var newViewData = {
                        ...emptyDto,
                        isBlocked: viewData.isBlocked,
                        evaluation: forSurvey || emptyDto.evaluation == null ? null : {
                            ...emptyDto.evaluation,
                            points: viewData.evaluation?.points ?? 0,
                            allowInKnowledgeTests: viewData.evaluation?.allowInKnowledgeTests ?? false,
                        },
                        survey: viewData.survey,
                        tags: viewData.tags
                    };
                    setCurrentTab(getDefaultTab(questionType));
                    setViewData(newViewData);
                    setTimeout(() => {
                        questionTextRef.current!.value = "";
                        questionTextRef.current?.focus();
                    }, 500);
                }
                else {
                    onClose(true);
                }
            }
            catch (e) {
                resolveErrors(e);
            }
            finally {
                setSaving(null);
            }
        }

        if (saving != null) postData();

    }, [saving]);

    ///
    useEffect(() => {
        setTimeout(() => {
            questionTextRef.current?.focus();
        }, 500);
    }, []);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    const disabled = saving != null || loading;

    ///
    const tabIcon = (show: boolean) => {
        return (
            <>
                {
                    show === true
                        ?
                        <MprcIconFa
                            icon={faTriangleExclamation}
                            color="warning"
                            fixedWidth
                            title={t("Error.AlertInvalidValuesTooltip", { ns: "shared" })}
                        />
                        :
                        <div>{/* Deixa em branco, para manter o mesmo height de quando o ícone é exibido */}</div>
                }
            </>
        );
    };

    ///
    const RenderTabs = () => {
        var tabs: IQuestionTab[] = [
            { value: "complement", name: t(forSurvey ? "Question.Edit.Tab.InstructionText" : "Question.Edit.Tab.ComplementText"), hasError: false },
        ];
        if (hasAlternative || hasStar) {
            tabs.push({ value: "alternative", name: t("Question.Edit.Tab.AlternativeText"), hasError: Object.values(postValidation.alternativeBox).filter((x) => x !== undefined).length != 0 });
        } else if (hasEssay) {
            tabs.push({ value: "essay", name: t("Question.Edit.Tab.EssayText"), hasError: boxValidation.essayBox == false });
        } else if (hasFillIn) {
            tabs.push({ value: "fillIn", name: t("Question.Edit.Tab.FillInText"), hasError: Object.values(postValidation.fillInBox).filter((x) => x !== undefined).length != 0 });
        }
        if (hasFeedback) {
            tabs.push({ value: "feedback", name: t("Question.Edit.Tab.FeedbackText"), hasError: boxValidation.feedbackBox == false });
        }
        if (hasClassification) {
            tabs.push({ value: "classification", name: t("Question.Edit.Tab.ClassificationText"), hasError: Object.values(postValidation.available).filter((x) => x !== undefined).length != 0 });
        }
        if (hasOtherPoints) {
            tabs.push({ value: "pointOthers", name: t("Question.Edit.Tab.OthersText"), hasError: boxValidation.pointBox == false || Object.values(postValidation.available).filter((x) => x !== undefined).length != 0 });
        }

        return (
            <MprcBarTabs sx={{ marginBottom: 3 }}
                variant="fullWidth"
                value={currentTab}
                onChange={handleSelectTab}>
                {
                    tabs.map((item) => <Tab key={item.value} value={item.value} label={item.name} icon={tabIcon(item.hasError)} />)
                }
            </MprcBarTabs>
        );
    };

    ///
    return (
        <Dialog open fullWidth maxWidth="sm" onClose={() => onClose(reloadOnClose.current)}>
            <MprcDialogTitleClose
                title={t(readonly ? "Question.Edit.Title" : isNewQuestion ? "Question.Edit.NewTitle" : "Question.Edit.EditTitle")}
                onClose={() => onClose(reloadOnClose.current)}
                shrinkPadding
            />
            <DialogContent>

                <MprcRequestIndicator type={loading ? "request" : "post"} visible={loading || saving != null} />

                <MprcRequestErrorDialog error={viewError} onClose={() => setViewError(null)} />

                <QuestionContext.Provider
                    value={{
                        viewData,
                        validationFields,
                        postValidation,
                        handleUpdateRecord,
                        handleUpdateEvaluation,
                        handleUpdateSurvey,
                        forSurvey,
                        readonly,
                        editMode
                    }}
                >
                    <MprcContainer margin="none">

                        <EditQuestionMainbox
                            questionTextRef={questionTextRef}
                        />

                        {RenderTabs()}

                        <Grid container direction="column" height="100%" minHeight={368}>
                            <Grid size="grow" width="100%">
                                {
                                    currentTab == "complement"
                                        ? <EditQuestionComplement />
                                        : currentTab == "alternative" && viewData.questionType == QuestionType.Star
                                            ? <EditQuestionStar />
                                            : currentTab == "alternative"
                                                ? <EditQuestionAlternative canSelectType={questionId == null} />
                                                : currentTab == "essay"
                                                    ? <EditQuestionEssay />
                                                    : currentTab == "fillIn"
                                                        ? <EditQuestionFillIn canSelectType={questionId == null} />
                                                        : currentTab == "feedback"
                                                            ? <EditQuestionFeedback />
                                                            : currentTab == "pointOthers"
                                                                ? <EditQuestionPointOthers
                                                                    onError={error => resolveErrors(error)}
                                                                />
                                                                : <EditQuestionClassification
                                                                    onError={error => resolveErrors(error)}
                                                                />
                                }
                            </Grid>
                        </Grid>

                    </MprcContainer>
                </QuestionContext.Provider>
            </DialogContent>
            <DialogActions>
                <Grid container spacing={1} alignItems="center" width="100%">
                    <Grid size="grow">
                        {
                            isNewQuestion && readonly != true &&
                            <MprcSwitch
                                label={t("Question.Edit.ContinueCreateLabel")}
                                checked={continueCreate}
                                onChange={(e) => setContinueCreate(e.target.checked)}
                            />
                        }
                    </Grid>
                    <Grid>
                        <Grid container spacing={1}>
                            {
                                showDraft &&
                                <Grid>
                                    <MprcButton
                                        variant="contained"
                                        color="warning"
                                        onClick={() => setSaving("draft")}
                                        disabled={disabled || !canSaveDraft}
                                    >
                                        <span>{t("Question.Edit.ButtonDraftText")}</span>
                                    </MprcButton>
                                </Grid>
                            }
                            <Grid>
                                <ButtonCancel variant="contained" onClick={() => onClose(reloadOnClose.current)} />
                            </Grid>
                            {
                                readonly != true && version == undefined &&
                                <Grid>
                                    <ButtonSave
                                        disabled={disabled || !canSave}
                                        variant="contained"
                                        onClick={() => setSaving("save")}
                                    />
                                </Grid>
                            }
                        </Grid>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    );
};
